import React, { useState, useRef, useEffect } from 'react';
import './DMCPackageCreator.css';
import "../../Supplier/HolidayDashboard.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faMinus, faImage, faChevronDown, faTimes, faTrashAlt, faCheckCircle, faTimesCircle, faPlane, faMapMarkerAlt, faHotel, faCalendarAlt, faBed, faUtensils, faSave, faGlobe
} from '@fortawesome/free-solid-svg-icons';
import DMCPackagePreview from './DMCPackagePreview';
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import Header from '../../Common/Layout/Header';
import DESTINATIONPLACES from '../../Data/DestinationPlaces.json';
import AIRPORTLIST from '../../Data/AirportList.json';
import AIRLINELIST from '../../Data/airlines.json';
import DMCPackageTab from '../../Components/AllTabs/DMCPackageTab';



const INCLUDES_OPTIONS = ["Hotel", "Flight", "Sightseeing", "Meals", "Transfers", "Visa", "Guide"];

const DMCPackageCreator = () => {
    const { user } = useAuth(); // Auth context for Company ID
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const editId = queryParams.get("id");
    const isEditMode = !!editId;
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('details'); // Set to 'dest' for testing
    const [btnLoading, setBtnLoading] = useState(false);
    const [rawHeroImage, setRawHeroImage] = useState(null);
    const [packageData, setPackageData] = useState({
        id: "00000000-0000-0000-0000-000000000000",
        companyId: user?.cid || "00000000-0000-0000-0000-000000000000",
        companyName: '',
        title: '',
        description: '',
        destinations: [],
        totalNights: 0,
        itinerary: [{ day: 1, cityCode: '', cityName: '', title: '', description: '' }],
        flights: { included: false, type: 0, options: [] },
        pricing: {
            currency: 'INR',
            landOnly: { double: 0, triple: 0, single: 0, childWithBed: 0, childWithoutBed: 0 },
            withFlight: { double: 0, triple: 0, single: 0, childWithBed: 0, childWithoutBed: 0 }
        },
        inclusions: [],
        exclusions: [],
        validity: { from: '', to: '' },
        status: 0,
        heroImage: null,
        includes: [],
        media: [],
        contentType: 1
    });

    const [tempIncl, setTempIncl] = useState('');
    const [tempExcl, setTempExcl] = useState('');
    const [searchTermCountry, setSearchTermCountry] = useState("");
    const [activeDropdown, setActiveDropdown] = useState({
        type: null, cIdx: null, pIdx: null, fIdx: null, sIdx: null
    });
    const [errors, setErrors] = useState({});
    const fileInputRef = useRef(null);

    const handleModeSwitch = (path) => {
        if (editId) {
            navigate(`${path}?id=${editId}`);
        } else {
            navigate(path);
        }
    };

   const fetchPackageForEdit = async () => {
        if (isEditMode) {
            setBtnLoading(true);
            try {
                const res = await apiService.getAll(`${config.GETDMCPACKAGEBYID}/${editId}`);
                console.log(res)
                if (res) {
                    
                    // --- FIX START: Format Flight Dates ---
                    // Helper to convert "2026-02-20T07:16:00Z" -> "2026-02-20T07:16"
                    const formatDateTime = (dateStr) => {
                        if (!dateStr) return '';
                        return dateStr.substring(0, 16); 
                    };

                    const formattedFlights = res.flights ? {
                        ...res.flights,
                        options: res.flights.options.map(opt => ({
                            ...opt,
                            segments: opt.segments.map(seg => ({
                                ...seg,
                                departureTime: formatDateTime(seg.departureTime),
                                arrivalTime: formatDateTime(seg.arrivalTime)
                            }))
                        }))
                    } : { included: false, type: 0, options: [] };
                    // --- FIX END ---

                    setPackageData(prev => ({
                        ...prev, 
                        ...res,  
                        validity: {
                            from: res.validity?.from?.split('T')[0] || '',
                            to: res.validity?.to?.split('T')[0] || ''
                        },
                        flights: formattedFlights, // <--- Assign the formatted flights here
                        includes: res.includes || [],
                        inclusions: res.inclusions || [],
                        exclusions: res.exclusions || [],
                        destinations: res.destinations || []
                    }));
                }
            } catch (err) {
                console.error("Fetch Error:", err);
            } finally {
                setBtnLoading(false);
            }
        }
    };
    useEffect(() => {
        fetchPackageForEdit();
    }, [isEditMode, editId]);

    const validate = () => {
        let tempErrors = {};
        if (!packageData.title || packageData.title.trim() === "") {
            tempErrors.title = "Please enter your package title.";
        }
        if (!packageData.validity.from) {
            tempErrors.validityFrom = "Please select validity from date.";
        }
        if (!packageData.validity.to) {
            tempErrors.validityTo = "Please select validity to date.";
        }

        setErrors(tempErrors);

        // If errors found, switch to details tab automatically
        if (Object.keys(tempErrors).length > 0) {
            setActiveTab('details');
            return false;
        }
        return true;
    };

    useEffect(() => {
        let count = 0;
        packageData.destinations.forEach(c => c.places.forEach(p => count += parseInt(p.nights || 0)));
        setPackageData(prev => ({ ...prev, totalNights: count }));
    }, [packageData.destinations]);

    const handleInputChange = (field, value) => {
        setPackageData(prev => ({ ...prev, [field]: value }));
    };

    const addListItem = (type) => {
        if (type === 'inc' && tempIncl) {
            setPackageData(prev => ({ ...prev, inclusions: [...prev.inclusions, tempIncl] }));
            setTempIncl('');
        } else if (type === 'exc' && tempExcl) {
            setPackageData(prev => ({ ...prev, exclusions: [...prev.exclusions, tempExcl] }));
            setTempExcl('');
        }
    };

    const updateItin = (idx, field, val) => {
        const newItin = [...packageData.itinerary];
        newItin[idx] = { ...newItin[idx], [field]: val };
        setPackageData(prev => ({ ...prev, itinerary: newItin }));
    };

    const addDay = () => {
        setPackageData(prev => ({
            ...prev,
            itinerary: [
                ...prev.itinerary,
                {
                    day: prev.itinerary.length + 1,
                    cityCode: '',
                    cityName: '',
                    title: '',
                    description: ''
                }
            ]
        }));
    };
    const addFlightSegment = (optIdx) => {
        const newOptions = [...packageData.flights.options];
        newOptions[optIdx].segments.push({
            from: '', // Added
            to: '',   // Added
            airline: '',
            flightNo: '',
            departureTime: '',
            arrivalTime: ''
        });
        setPackageData(prev => ({ ...prev, flights: { ...prev.flights, options: newOptions } }));
    };

    const updateFlightSegment = (optIdx, segIdx, field, val) => {
        const newOptions = [...packageData.flights.options];
        newOptions[optIdx].segments[segIdx][field] = val;
        setPackageData(prev => ({ ...prev, flights: { ...prev.flights, options: newOptions } }));
    };
    const addFlightOption = () => {
        const newOption = {
            optionId: Date.now().toString(),
            from: '', to: '', returnFrom: '', returnTo: '',
            airline: '', cabin: 'Economy', baggage: '', pricePerPerson: 0,
            segments: [{ from: '', to: '', airline: '', flightNo: '', departureTime: '', arrivalTime: '' }]
        };
        setPackageData(prev => ({
            ...prev,
            flights: { ...prev.flights, options: [...prev.flights.options, newOption] }
        }));
    };

    const updateFlightOption = (idx, field, val) => {
        const newOptions = [...packageData.flights.options];
        newOptions[idx][field] = val;
        setPackageData(prev => ({
            ...prev,
            flights: { ...prev.flights, options: newOptions }
        }));
    };

    const removeFlightOption = (idx) => {
        setPackageData(prev => ({
            ...prev,
            flights: { ...prev.flights, options: prev.flights.options.filter((_, i) => i !== idx) }
        }));
    };

    const removeDay = (idx) => {
        const filtered = packageData.itinerary.filter((_, i) => i !== idx);
        const reindexed = filtered.map((d, i) => ({ ...d, day: i + 1 }));
        setPackageData(prev => ({ ...prev, itinerary: reindexed }));
    };
    const addCountry = () => {
        const newCountry = { countryCode: "", countryName: "", places: [] };
        setPackageData(prev => ({ ...prev, destinations: [...prev.destinations, newCountry] }));
    };

    const addPlace = (cIdx) => {
        const newDest = [...packageData.destinations];
        newDest[cIdx].places.push({ cityCode: "", cityName: "", nights: 1, sequence: newDest[cIdx].places.length + 1, hotels: [] });
        setPackageData(prev => ({ ...prev, destinations: newDest }));
    };

    const addHotel = (cIdx, pIdx) => {
        const newDest = [...packageData.destinations];
        newDest[cIdx].places[pIdx].hotels.push({
            cityCode: newDest[cIdx].places[pIdx].cityCode || "CODE",
            hotelCategory: 3,
            hotelOptions: [{ name: "", roomType: "", mealPlan: "" }]
        });
        setPackageData(prev => ({ ...prev, destinations: newDest }));
    };

    const updateHotelOption = (cIdx, pIdx, hIdx, optIdx, field, val) => {
        const newDest = [...packageData.destinations];
        newDest[cIdx].places[pIdx].hotels[hIdx].hotelOptions[optIdx][field] = val;
        setPackageData(prev => ({ ...prev, destinations: newDest }));
    };

    const removeHotel = (cIdx, pIdx, hIdx) => {
        const newDest = [...packageData.destinations];
        newDest[cIdx].places[pIdx].hotels = newDest[cIdx].places[pIdx].hotels.filter((_, i) => i !== hIdx);
        setPackageData(prev => ({ ...prev, destinations: newDest }));
    };

    const handlePricingChange = (category, field, value) => {
        setPackageData(prev => ({
            ...prev,
            pricing: { ...prev.pricing, [category]: { ...prev.pricing[category], [field]: value } }
        }));
    };
    const handleSavePackage = async () => {
        if (!validate()) return;

        setBtnLoading(true);
        const toNum = (val) => parseFloat(val) || 0;

        // --- 1. Clean Structured Data (Keep your existing logic) ---
        const cleanDestinations = packageData.destinations.map(dest => ({
            ...dest,
            places: dest.places.map(place => ({
                ...place,
                nights: parseInt(place.nights) || 0,
                sequence: parseInt(place.sequence) || 0,
                hotels: place.hotels.map(hotel => ({
                    ...hotel,
                    hotelCategory: parseInt(hotel.hotelCategory) || 0,
                    hotelOptions: hotel.hotelOptions.map(opt => ({
                        ...opt,
                        name: opt.name || "TBD",
                        roomType: opt.roomType || "Standard",
                        mealPlan: opt.mealPlan || "BB"
                    }))
                }))
            }))
        }));

        const cleanFlights = {
            ...packageData.flights,
            type: parseInt(packageData.flights.type) || 0,
            options: packageData.flights.options.map(opt => ({
                ...opt,
                pricePerPerson: toNum(opt.pricePerPerson),
                segments: opt.segments.map(seg => ({
                    ...seg,
                    departureTime: seg.departureTime ? new Date(seg.departureTime).toISOString() : new Date().toISOString(),
                    arrivalTime: seg.arrivalTime ? new Date(seg.arrivalTime).toISOString() : new Date().toISOString(),
                }))
            }))
        };

        // const cleanPricing = {
        //     currency: packageData.pricing.currency,
        //     landOnly: {
        //         double: toNum(packageData.pricing.landOnly.double),
        //         triple: toNum(packageData.pricing.landOnly.triple),
        //         single: toNum(packageData.pricing.landOnly.single),
        //         childWithBed: toNum(packageData.pricing.landOnly.childWithBed),
        //         childWithoutBed: toNum(packageData.pricing.landOnly.childWithoutBed),
        //     },
        //     withFlight: {
        //         double: toNum(packageData.pricing.withFlight.double),
        //         triple: toNum(packageData.pricing.withFlight.triple),
        //         single: toNum(packageData.pricing.withFlight.single),
        //         childWithBed: toNum(packageData.pricing.withFlight.childWithBed),
        //         childWithoutBed: toNum(packageData.pricing.withFlight.childWithoutBed),
        //     }
        // };
        const userEnteredPrices = {
            double: toNum(packageData.pricing.landOnly.double),
            triple: toNum(packageData.pricing.landOnly.triple),
            single: toNum(packageData.pricing.landOnly.single),
            childWithBed: toNum(packageData.pricing.landOnly.childWithBed),
            childWithoutBed: toNum(packageData.pricing.landOnly.childWithoutBed),
        };

        // B. Define a "Zero" object for the category we are NOT using
        const zeroPrices = { double: 0, triple: 0, single: 0, childWithBed: 0, childWithoutBed: 0 };

        // C. Route the data based on the "Flights Included" checkbox
        const cleanPricing = {
            currency: packageData.pricing.currency,

            // IF Flights Included -> Save User Prices to 'withFlight', set 'landOnly' to 0
            // IF Flights Excluded -> Save User Prices to 'landOnly', set 'withFlight' to 0
            landOnly: packageData.flights.included ? zeroPrices : userEnteredPrices,
            withFlight: packageData.flights.included ? userEnteredPrices : zeroPrices
        };


        // --- 2. Build the DmcPackageDto Object ---
        const payload = {
            id: packageData.id || "00000000-0000-0000-0000-000000000000",
            companyId: user?.cid,
            companyName: packageData.companyName,
            title: packageData.title,
            description: packageData.description,
            includes: packageData.includes || [],
            destinations: cleanDestinations,
            totalNights: parseInt(packageData.totalNights) || 0,
            itinerary: packageData.itinerary.map(itin => ({ ...itin, day: parseInt(itin.day) || 0 })),
            flights: cleanFlights,
            pricing: cleanPricing,
            inclusions: packageData.inclusions,
            exclusions: packageData.exclusions,
            validity: {
                from: new Date(packageData.validity.from).toISOString(),
                to: new Date(packageData.validity.to).toISOString()
            },
            status: packageData.status,
            contentType: 1, // 1 = Structured (Manual Entry)
            createdAt: new Date().toISOString(),
            media: { images: [], itineraryPdf: null }
        };

        try {
            const formData = new FormData();
            formData.append("PackageJson", JSON.stringify(payload));
            
            // Only append file if a NEW one was selected
            if (rawHeroImage) {
                formData.append("UploadImages", rawHeroImage);
            }
            if (isEditMode) {
               console.log("Updating package with payload:", payload);
                await apiService.update(`${config.UPDATEDMCPACKAGE}/${payload.id}`, formData);

                await fetchPackageForEdit();
                // navigate("/dmcpackageslist");
            } else {
                await apiService.create(config.CREATEDMCPACKAGE, formData);
                navigate("/dmcpackageslist");
            }
        } catch (error) {
            console.error("API Error:", error);
            // Error handling is already managed by your apiService.handleError
        } finally {
            setBtnLoading(false);
        }
    };

    const handleCountrySelect = (cIdx, selectedDest) => {
        const d = [...packageData.destinations];
        d[cIdx].countryName = selectedDest.destination;
        d[cIdx].countryCode = selectedDest.destinationId; // or some code logic
        setPackageData(prev => ({ ...prev, destinations: d }));
        setActiveDropdown({ type: null, cIdx: null, pIdx: null });
    };

    const handlePlaceSelect = (cIdx, pIdx, selectedPlace) => {
        const d = [...packageData.destinations];
        d[cIdx].places[pIdx].cityName = selectedPlace.placeName;
        d[cIdx].places[pIdx].cityCode = selectedPlace.placeId;
        setPackageData(prev => ({ ...prev, destinations: d }));
        setActiveDropdown({ type: null, cIdx: null, pIdx: null });
    };
    // --- SMART AUTOCOMPLETE HELPERS ---

    const handleCountryFieldSelect = (cIdx, item, type) => {
        const d = [...packageData.destinations];

        if (type === 'country') {
            // User selected a Country (e.g., India)
            d[cIdx].countryName = item.destination;
            d[cIdx].countryCode = item.destinationId;
        } else {
            // User selected a Place (e.g., Delhi) in the Country slot
            // 1. Find the parent country from MASTER DATA
            const parentCountry = DESTINATIONPLACES.find(dest => dest.destinationId === item.destinationId);

            d[cIdx].countryName = parentCountry.destination;
            d[cIdx].countryCode = parentCountry.destinationId;

            // 2. Automatically add this place to the cities list if not already there
            const cityExists = d[cIdx].places.some(p => p.cityName === item.placeName);
            if (!cityExists) {
                // If it's the first empty place row, update it, otherwise add new
                if (d[cIdx].places.length === 1 && !d[cIdx].places[0].cityName) {
                    d[cIdx].places[0].cityName = item.placeName;
                    d[cIdx].places[0].cityCode = item.placeId;
                } else {
                    d[cIdx].places.push({
                        cityCode: item.placeId,
                        cityName: item.placeName,
                        nights: 1,
                        sequence: d[cIdx].places.length + 1,
                        hotels: []
                    });
                }
            }
        }
        setPackageData(prev => ({ ...prev, destinations: d }));
        setActiveDropdown({ type: null, cIdx: null, pIdx: null });
    };

    return (
        <div className="layout-wrapper">

            <Header user={user} activeSection="holidays" />
            <div className="p-2">
                <DMCPackageTab pageName="DMCPACKAGECREATE" />
            </div>
            <div className="holidaydashboard-dashboard">

                {/* WRAP YOUR EXISTING FORM CONTENT */}
                <div className="holidaydashboard-content-wrapper">
                    <div className="container-fluid">
                        <div className="card shadow-sm border-0">
                            <div className="dmc-pkg-main-wrapper">
                                <div className="dmc-pkg-form-container">
                                    <div className="dmc-pkg-tabs mb-4">
                                        <button
                                            className="dmc-pkg-tab-link is-active"
                                            style={{ borderBottom: '2px solid #2563eb', color: '#2563eb' }}
                                        >
                                            Manual Entry
                                        </button>
                                        <button
                                            className="dmc-pkg-tab-link"
                                            onClick={() => handleModeSwitch('/dmccreate-pdf')}
                                        >
                                            Upload PDF Only
                                        </button>
                                    </div>
                                    <div className="dmc-pkg-card">
                                        <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                                            <h2 className="dmc-pkg-section-title mb-0">
                                                {isEditMode ? "Update Package" : "Create Package"}
                                            </h2>
                                            {/* CREATE BUTTON ADDED HERE */}
                                            <button className="btn btn-primary d-flex align-items-center gap-2" onClick={handleSavePackage} disabled={btnLoading}>
                                                {btnLoading ? "Processing..." : <><FontAwesomeIcon icon={faSave} /> {isEditMode ? "Update Package" : "Create Package"}</>}
                                            </button>
                                        </div>


                                        <div className="dmc-pkg-tabs">
                                            <button className={`dmc-pkg-tab-link ${activeTab === 'details' ? 'is-active' : ''}`} onClick={() => setActiveTab('details')}>Details</button>
                                            <button className={`dmc-pkg-tab-link ${activeTab === 'dest' ? 'is-active' : ''}`} onClick={() => setActiveTab('dest')}>Destinations</button>
                                            <button className={`dmc-pkg-tab-link ${activeTab === 'itinerary' ? 'is-active' : ''}`} onClick={() => setActiveTab('itinerary')}>Itinerary</button>
                                            <button className={`dmc-pkg-tab-link ${activeTab === 'prices' ? 'is-active' : ''}`} onClick={() => setActiveTab('prices')}>Prices</button>
                                            <button className={`dmc-pkg-tab-link ${activeTab === 'flights' ? 'is-active' : ''}`} onClick={() => setActiveTab('flights')}>Flights</button>
                                            <button className={`dmc-pkg-tab-link ${activeTab === 'policy' ? 'is-active' : ''}`} onClick={() => setActiveTab('policy')}>Inclusions & Exclusions</button>
                                        </div>

                                        <div className="dmc-pkg-form-body">
                                            {/* DESTINATIONS TAB */}
                                            {activeTab === 'details' && (
                                                <div className="dmc-pkg-tab-content animate-fade">
                                                    <div className="dmc-pkg-field-group">
                                                        <label className="dmc-pkg-label">Package Title *</label>
                                                        <input
                                                            className={`dmc-pkg-input ${errors.title ? 'dmc-is-invalid' : ''}`}
                                                            placeholder="(e.g. Package to Dubai)"
                                                            type="text"
                                                            value={packageData.title}
                                                            onChange={(e) => {
                                                                handleInputChange('title', e.target.value);
                                                                if (errors.title) setErrors({ ...errors, title: null }); // Clear error as user types
                                                            }}
                                                        />
                                                        {errors.title && <span className="dmc-error-message">{errors.title}</span>}
                                                    </div>
                                                    <div className="dmc-pkg-grid-row">
                                                        <div className="dmc-pkg-field-group">
                                                            <label className="dmc-pkg-label"><FontAwesomeIcon icon={faCalendarAlt} /> Validity From</label>
                                                            <input
                                                                className={`dmc-pkg-input ${errors.validityFrom ? 'dmc-is-invalid' : ''}`}
                                                                type="date"
                                                                value={packageData.validity.from}
                                                                onChange={(e) => {
                                                                    handleInputChange('validity', { ...packageData.validity, from: e.target.value });
                                                                    if (errors.validityFrom) setErrors({ ...errors, validityFrom: null });
                                                                }}
                                                            />
                                                            {errors.validityFrom && <span className="dmc-error-message">{errors.validityFrom}</span>}
                                                        </div>
                                                        <div className="dmc-pkg-field-group">
                                                            <label className="dmc-pkg-label"><FontAwesomeIcon icon={faCalendarAlt} /> Validity To</label>
                                                            <input
                                                                className={`dmc-pkg-input ${errors.validityTo ? 'dmc-is-invalid' : ''}`}
                                                                type="date"
                                                                value={packageData.validity.to}
                                                                onChange={(e) => {
                                                                    handleInputChange('validity', { ...packageData.validity, to: e.target.value });
                                                                    if (errors.validityTo) setErrors({ ...errors, validityTo: null });
                                                                }}
                                                            />
                                                            {errors.validityTo && <span className="dmc-error-message">{errors.validityTo}</span>}
                                                        </div>

                                                        <div className="dmc-pkg-field-group">
                                                            <label className="dmc-pkg-label">Includes</label>
                                                            <select
                                                                className="dmc-pkg-input"
                                                                value="" // Keeps the dropdown showing "Select..." after an item is added
                                                                onChange={(e) => {
                                                                    if (e.target.value) {
                                                                        setPackageData(prev => ({ ...prev, includes: [...new Set([...prev.includes, e.target.value])] }))
                                                                    }
                                                                }}
                                                            >
                                                                <option value="" disabled>Select...</option>
                                                                {INCLUDES_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                                            </select>

                                                            {/* --- DISPLAY SELECTED BADGES HERE --- */}
                                                            <div className="dmc-pkg-tag-container" style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                                {packageData.includes.map(tag => (
                                                                    <span key={tag} className="dmc-pkg-tag blue">
                                                                        {tag}
                                                                        <FontAwesomeIcon
                                                                            icon={faTimes}
                                                                            style={{ marginLeft: '8px', cursor: 'pointer', fontSize: '10px' }}
                                                                            onClick={() => setPackageData(prev => ({
                                                                                ...prev,
                                                                                includes: prev.includes.filter(t => t !== tag)
                                                                            }))}
                                                                        />
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="dmc-pkg-flex-row">
                                                        <div className="dmc-pkg-image-upload-section">
                                                            <div className="dmc-pkg-upload-box" onClick={() => fileInputRef.current.click()}>
                                                                {packageData.heroImage ? <img src={packageData.heroImage} alt="Cover" className="dmc-pkg-uploaded-img" /> : <span>+ Upload Image</span>}
                                                                <input
                                                                    type="file"
                                                                    ref={fileInputRef}
                                                                    hidden
                                                                    onChange={(e) => {
                                                                        const file = e.target.files[0];
                                                                        if (file) {
                                                                            setRawHeroImage(file); // Store the actual file object
                                                                            const reader = new FileReader();
                                                                            reader.onload = (ev) => handleInputChange('heroImage', ev.target.result); // Base64 for preview
                                                                            reader.readAsDataURL(file);
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                        <textarea className="dmc-pkg-input dmc-pkg-textarea dmc-pkg-flex-grow" placeholder="Short summary..." value={packageData.description} onChange={(e) => handleInputChange('description', e.target.value)} />
                                                    </div>
                                                </div>
                                            )}
                                            {activeTab === 'dest' && (
                                                <div className="dmc-pkg-tab-content animate-fade">
                                                    {packageData.destinations.map((country, cIdx) => (
                                                        <div key={cIdx} className="dmc-pkg-itin-item-card country-card">
                                                            <div style={{ padding: '0 15px', marginTop: '10px', marginLeft: '25px', marginBottom: '5px' }}>
                                                                <span style={{ fontSize: '11px', fontWeight: '800', color: '#2469caff', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                    Destination
                                                                </span>
                                                            </div>
                                                            <div className="dmc-pkg-itin-header" style={{ position: 'relative' }}>
                                                                <FontAwesomeIcon icon={faMapMarkerAlt} className="dmc-pkg-pin red-pin" />


                                                                <div className="dmc-autocomplete-wrapper" style={{ flex: 1 }}>
                                                                    <input
                                                                        className="dmc-pkg-dest-country-input"
                                                                        placeholder="Search Country or Place (e.g. India or Delhi)"
                                                                        value={country.countryName}
                                                                        onChange={(e) => {
                                                                            const d = [...packageData.destinations];
                                                                            d[cIdx].countryName = e.target.value;
                                                                            setPackageData(prev => ({ ...prev, destinations: d }));
                                                                            setActiveDropdown({ type: 'country', cIdx, pIdx: null });
                                                                        }}
                                                                        onFocus={() => setActiveDropdown({ type: 'country', cIdx, pIdx: null })}
                                                                    />
                                                                    {activeDropdown.type === 'country' && activeDropdown.cIdx === cIdx && country.countryName.length > 0 && (
                                                                        <ul className="dmc-
                                                                        dmc-autocomplete-list">
                                                                            {/* 1. Show matching Countries */}
                                                                            {DESTINATIONPLACES.filter(item => item.destination.toLowerCase().includes(country.countryName.toLowerCase()))
                                                                                .map(item => (
                                                                                    <li key={item.destinationId} onClick={() => handleCountryFieldSelect(cIdx, item, 'country')}>
                                                                                        <FontAwesomeIcon icon={faGlobe} style={{ marginRight: '8px', color: '#64748b' }} />
                                                                                        {item.destination} (Country)
                                                                                    </li>
                                                                                ))
                                                                            }
                                                                            {/* 2. Show matching Places (Smart Logic) */}
                                                                            {DESTINATIONPLACES.flatMap(dest => dest.places.map(p => ({ ...p, parentName: dest.destination })))
                                                                                .filter(p => p.placeName.toLowerCase().includes(country.countryName.toLowerCase()))
                                                                                .slice(0, 5) // Limit results for better performance
                                                                                .map(p => (
                                                                                    <li key={p.placeId} onClick={() => handleCountryFieldSelect(cIdx, p, 'place')}>
                                                                                        <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: '8px', color: '#ef4444' }} />
                                                                                        {p.placeName}, {p.parentName}
                                                                                    </li>
                                                                                ))
                                                                            }
                                                                        </ul>
                                                                    )}
                                                                </div>

                                                                <button className="dmc-pkg-itin-delete" onClick={() => setPackageData(prev => ({ ...prev, destinations: prev.destinations.filter((_, i) => i !== cIdx) }))}>
                                                                    <FontAwesomeIcon icon={faTrashAlt} />
                                                                </button>
                                                            </div>

                                                            <div className="places-list">
                                                                {country.places.map((place, pIdx) => (

                                                                    <div key={pIdx} className="dmc-pkg-place-item-box">
                                                                        <div style={{ marginBottom: '5px' }}>
                                                                            <span style={{ fontSize: '10px', fontWeight: '700', color: '#2866beff', textTransform: 'uppercase' }}>
                                                                                Place
                                                                            </span>
                                                                        </div>
                                                                        <div className="dmc-pkg-place-row-main" style={{ position: 'relative' }}>

                                                                            {/* --- CITY SEARCH (Filtered by Parent Country) --- */}
                                                                            <div className="dmc-autocomplete-wrapper" style={{ flex: 3 }}>
                                                                                <input
                                                                                    placeholder="Place Name (e.g. Delhi)"
                                                                                    className="dmc-pkg-input city-input"
                                                                                    value={place.cityName}
                                                                                    onChange={(e) => {
                                                                                        const d = [...packageData.destinations];
                                                                                        d[cIdx].places[pIdx].cityName = e.target.value;
                                                                                        setPackageData(prev => ({ ...prev, destinations: d }));
                                                                                        setActiveDropdown({ type: 'place', cIdx, pIdx });
                                                                                    }}
                                                                                    onFocus={() => setActiveDropdown({ type: 'place', cIdx, pIdx })}
                                                                                />
                                                                                {activeDropdown.type === 'place' && activeDropdown.cIdx === cIdx && activeDropdown.pIdx === pIdx && place.cityName.length > 0 && (
                                                                                    <ul className="dmc-autocomplete-list">
                                                                                        {DESTINATIONPLACES.find(dest => dest.destination === country.countryName)?.places
                                                                                            .filter(p => p.placeName.toLowerCase().includes(place.cityName.toLowerCase()))
                                                                                            .map(p => (
                                                                                                <li key={p.placeId} onClick={() => {
                                                                                                    const d = [...packageData.destinations];
                                                                                                    d[cIdx].places[pIdx].cityName = p.placeName;
                                                                                                    d[cIdx].places[pIdx].cityCode = p.placeId;
                                                                                                    setPackageData(prev => ({ ...prev, destinations: d }));
                                                                                                    setActiveDropdown({ type: null, cIdx: null, pIdx: null });
                                                                                                }}>
                                                                                                    {p.placeName}
                                                                                                </li>
                                                                                            ))
                                                                                        }
                                                                                    </ul>
                                                                                )}
                                                                            </div>

                                                                            <div className="nights-counter-small">
                                                                                <input type="number" value={place.nights} onChange={(e) => {
                                                                                    const d = [...packageData.destinations];
                                                                                    d[cIdx].places[pIdx].nights = e.target.value;
                                                                                    setPackageData(prev => ({ ...prev, destinations: d }));
                                                                                }} />
                                                                                <span style={{ color: '#332ae1ff' }}>Nights</span>

                                                                            </div>
                                                                            <button className="dmc-pkg-add-hotel-btn" onClick={() => addHotel(cIdx, pIdx)}>
                                                                                <FontAwesomeIcon icon={faHotel} /> + Hotel
                                                                            </button>
                                                                            <button className="dmc-pkg-itin-delete" onClick={() => {
                                                                                const d = [...packageData.destinations];
                                                                                d[cIdx].places = d[cIdx].places.filter((_, i) => i !== pIdx);
                                                                                setPackageData(prev => ({ ...prev, destinations: d }));
                                                                            }}><FontAwesomeIcon icon={faTimes} /></button>
                                                                        </div>

                                                                        {place.hotels.length > 0 && (

                                                                            <div className="dmc-pkg-nested-hotels-area">
                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', color: '#334155' }}>
                                                                                    <FontAwesomeIcon icon={faHotel} style={{ fontSize: '12px', color: '#332ae1ff' }} />
                                                                                    <span style={{ fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Accommodation / Hotels</span>
                                                                                </div>
                                                                                {place.hotels.map((hotel, hIdx) => (
                                                                                    <div key={hIdx} className="dmc-pkg-hotel-edit-row">
                                                                                        <div className="hotel-opt-grid">
                                                                                            <input placeholder="Hotel Name" value={hotel.hotelOptions[0].name} onChange={(e) => updateHotelOption(cIdx, pIdx, hIdx, 0, 'name', e.target.value)} />
                                                                                            <input placeholder="Meal Plan" value={hotel.hotelOptions[0].mealPlan} onChange={(e) => updateHotelOption(cIdx, pIdx, hIdx, 0, 'mealPlan', e.target.value)} />
                                                                                            <select value={hotel.hotelCategory} onChange={(e) => {
                                                                                                const d = [...packageData.destinations];
                                                                                                d[cIdx].places[pIdx].hotels[hIdx].hotelCategory = e.target.value;
                                                                                                setPackageData(prev => ({ ...prev, destinations: d }));
                                                                                            }}>
                                                                                                <option value="3">3 Star</option>
                                                                                                <option value="4">4 Star</option>
                                                                                                <option value="5">5 Star</option>
                                                                                            </select>
                                                                                            <button className="remove-hotel-btn" onClick={() => removeHotel(cIdx, pIdx, hIdx)}>
                                                                                                <FontAwesomeIcon icon={faTrashAlt} />
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}

                                                                    </div>
                                                                ))}
                                                                <button className="dmc-pkg-add-place-link" onClick={() => addPlace(cIdx)}>
                                                                    + Add Place to {country.countryName || 'Destination'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    <button className="dmc-pkg-add-day-btn-dashed" onClick={addCountry}>+ Add Destination</button>
                                                </div>
                                            )}
                                            {activeTab === 'policy' && (
                                                <div className="dmc-pkg-tab-content animate-fade">
                                                    <div className="dmc-pkg-grid-row">
                                                        {/* Inclusions Column */}
                                                        <div className="dmc-pkg-field-group">
                                                            <label className="dmc-pkg-label">Inclusions</label>
                                                            <div className="dmc-pkg-action-input">
                                                                <input
                                                                    className="dmc-pkg-input"
                                                                    type="text"
                                                                    placeholder="Type inclusion..."
                                                                    value={tempIncl}
                                                                    onChange={(e) => setTempIncl(e.target.value)}
                                                                    onKeyDown={(e) => e.key === 'Enter' && addListItem('inc')}
                                                                />
                                                                <button className="dmc-pkg-btn-green" onClick={() => addListItem('inc')}>
                                                                    <FontAwesomeIcon icon={faPlus} />
                                                                </button>
                                                            </div>
                                                            <div className="dmc-pkg-list-box">
                                                                {packageData.inclusions.length === 0 && <div className="dmc-pkg-empty-list">No inclusions added yet</div>}
                                                                {packageData.inclusions.map((item, idx) => (
                                                                    <div key={idx} className="dmc-pkg-list-item">
                                                                        <div className="dmc-pkg-list-text"><FontAwesomeIcon icon={faCheckCircle} className="green-icon" /> {item}</div>
                                                                        <FontAwesomeIcon icon={faTimes} className="remove-item" onClick={() => setPackageData(prev => ({ ...prev, inclusions: prev.inclusions.filter((_, i) => i !== idx) }))} />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>

                                                        {/* Exclusions Column */}
                                                        <div className="dmc-pkg-field-group">
                                                            <label className="dmc-pkg-label">Exclusions</label>
                                                            <div className="dmc-pkg-action-input">
                                                                <input
                                                                    className="dmc-pkg-input"
                                                                    type="text"
                                                                    placeholder="Type exclusion..."
                                                                    value={tempExcl}
                                                                    onChange={(e) => setTempExcl(e.target.value)}
                                                                    onKeyDown={(e) => e.key === 'Enter' && addListItem('exc')}
                                                                />
                                                                <button className="dmc-pkg-btn-red" onClick={() => addListItem('exc')}>
                                                                    <FontAwesomeIcon icon={faPlus} />
                                                                </button>
                                                            </div>
                                                            <div className="dmc-pkg-list-box">
                                                                {packageData.exclusions.length === 0 && <div className="dmc-pkg-empty-list">No exclusions added yet</div>}
                                                                {packageData.exclusions.map((item, idx) => (
                                                                    <div key={idx} className="dmc-pkg-list-item">
                                                                        <div className="dmc-pkg-list-text"><FontAwesomeIcon icon={faTimesCircle} className="red-icon" /> {item}</div>
                                                                        <FontAwesomeIcon icon={faTimes} className="remove-item" onClick={() => setPackageData(prev => ({ ...prev, exclusions: prev.exclusions.filter((_, i) => i !== idx) }))} />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {activeTab === 'itinerary' && (
                                                <div className="dmc-pkg-tab-content animate-fade">
                                                    {packageData.itinerary.map((day, idx) => (
                                                        <div key={idx} className="dmc-pkg-itin-item-card">
                                                            <div className="dmc-pkg-itin-header">
                                                                <div className="dmc-pkg-itin-circle">D{day.day}</div>

                                                                {/* New City Info Row */}
                                                                {/* <div className="dmc-pkg-itin-city-row">
                        <input 
                            className="dmc-pkg-itin-small-input"
                            type="text" 
                            placeholder="Place Name" 
                            value={day.cityName} 
                            onChange={(e) => updateItin(idx, 'cityName', e.target.value)} 
                        />
                        <input 
                            className="dmc-pkg-itin-small-input"
                            type="text" 
                            placeholder="City Code" 
                            style={{width: '100px'}}
                            value={day.cityCode} 
                            onChange={(e) => updateItin(idx, 'cityCode', e.target.value)} 
                        />
                    </div> */}

                                                                <input
                                                                    className="dmc-pkg-itin-title-input"
                                                                    type="text"
                                                                    placeholder="Title (e.g. Arrival in Dubai)"
                                                                    value={day.title}
                                                                    onChange={(e) => updateItin(idx, 'title', e.target.value)}
                                                                />
                                                                <button className="dmc-pkg-itin-delete" onClick={() => removeDay(idx)}>
                                                                    <FontAwesomeIcon icon={faTrashAlt} />
                                                                </button>
                                                            </div>
                                                            <div className="dmc-pkg-itin-body">
                                                                <textarea
                                                                    className="dmc-pkg-itin-desc-input"
                                                                    placeholder="Description of activities..."
                                                                    value={day.description}
                                                                    onChange={(e) => updateItin(idx, 'description', e.target.value)}
                                                                />
                                                            </div>
                                                        </div>
                                                    ))}

                                                    <button className="dmc-pkg-add-day-btn-dashed" onClick={addDay}>
                                                        <FontAwesomeIcon icon={faPlus} /> Add Day {packageData.itinerary.length + 1}
                                                    </button>
                                                </div>
                                            )}

                                            {activeTab === 'prices' && (
                                                <div className="dmc-pkg-tab-content animate-fade">
                                                    {/* Currency Selector */}
                                                    <div className="dmc-pkg-field-group" style={{ maxWidth: '200px' }}>
                                                        <label className="dmc-pkg-label">Currency</label>
                                                        <div className="dmc-pkg-select-wrapper">
                                                            <select
                                                                className="dmc-pkg-input dmc-pkg-full-width"
                                                                value={packageData.pricing.currency}
                                                                onChange={(e) => setPackageData(prev => ({ ...prev, pricing: { ...prev.pricing, currency: e.target.value } }))}
                                                            >
                                                                <option value="USD">USD - Dollar</option>
                                                                <option value="INR">INR - Rupee</option>
                                                                <option value="AED">AED - Dirham</option>
                                                            </select>
                                                            <FontAwesomeIcon icon={faChevronDown} className="dmc-pkg-chevron" />
                                                        </div>
                                                    </div>

                                                    {/* Land Only Section */}
                                                    <div className="dmc-pkg-price-card-edit">
                                                        {/* <h3 className="dmc-pkg-sub-section-title">Land Only Pricing</h3> */}
                                                        <h3 className="dmc-pkg-sub-section-title">Pricing</h3>
                                                        <div className="dmc-pkg-pricing-grid">
                                                            {[
                                                                { label: 'Double', field: 'double' },
                                                                { label: 'Triple', field: 'triple' },
                                                                { label: 'Single', field: 'single' },
                                                                { label: 'Child With Bed', field: 'childWithBed' },
                                                                { label: 'Child No Bed', field: 'childWithoutBed' }
                                                            ].map(item => (
                                                                <div key={item.field} className="dmc-pkg-field-group">
                                                                    <label className="dmc-pkg-label">{item.label}</label>
                                                                    <input
                                                                        className="dmc-pkg-input"
                                                                        type="number"
                                                                        value={packageData.pricing.landOnly[item.field]}
                                                                        onChange={(e) => handlePricingChange('landOnly', item.field, e.target.value)}
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    {/* With Flight Section */}
                                                    {/* <div className="dmc-pkg-price-card-edit flight-pricing-accent">
            <h3 className="dmc-pkg-sub-section-title">Pricing With Flights</h3>
            <div className="dmc-pkg-pricing-grid">
                {[
                    { label: 'Double', field: 'double' },
                    { label: 'Triple', field: 'triple' },
                    { label: 'Single', field: 'single' },
                    { label: 'Child With Bed', field: 'childWithBed' },
                    { label: 'Child No Bed', field: 'childWithoutBed' }
                ].map(item => (
                    <div key={item.field} className="dmc-pkg-field-group">
                        <label className="dmc-pkg-label">{item.label}</label>
                        <input 
                            className="dmc-pkg-input" 
                            type="number" 
                            value={packageData.pricing.withFlight[item.field]} 
                            onChange={(e) => handlePricingChange('withFlight', item.field, e.target.value)}
                        />
                    </div>
                ))}
            </div>
        </div> */}
                                                </div>
                                            )}
                                            {activeTab === 'flights' && (
                                                <div className="dmc-pkg-tab-content animate-fade">
                                                    <div className="dmc-pkg-flight-settings">
                                                        <label className="dmc-pkg-switch-container">
                                                            <input type="checkbox" checked={packageData.flights.included} onChange={(e) => setPackageData(prev => ({ ...prev, flights: { ...prev.flights, included: e.target.checked } }))} />
                                                            <span className="dmc-pkg-switch-label">Flights Included in Package</span>
                                                        </label>
                                                    </div>

                                                    {packageData.flights.options.map((flight, idx) => (
                                                        <div key={flight.optionId} className="dmc-pkg-itin-item-card flight-card">
                                                            {/* Header From/To logic remains same ... */}
                                                            <div className="dmc-pkg-itin-header">
                                                                <div className="dmc-pkg-itin-circle"><FontAwesomeIcon icon={faPlane} /></div>
                                                                <div className="dmc-pkg-flight-route-header">
                                                                    <div className="dmc-autocomplete-wrapper">
                                                                        <input placeholder="From(HYD)" value={flight.from} onChange={(e) => { updateFlightOption(idx, 'from', e.target.value); setActiveDropdown({ type: 'from', fIdx: idx }); }} onFocus={() => setActiveDropdown({ type: 'from', fIdx: idx })} />
                                                                        {activeDropdown.type === 'from' && activeDropdown.fIdx === idx && flight.from.length > 0 && (
                                                                            <ul className="dmc-autocomplete-list">
                                                                                {AIRPORTLIST.filter(item => item.cityName?.toLowerCase().includes(flight.from.toLowerCase()) || item.airportCode?.toLowerCase().includes(flight.from.toLowerCase())).slice(0, 8).map(item => (
                                                                                    <li key={item.airportCode} onClick={() => { updateFlightOption(idx, 'from', item.airportCode); setActiveDropdown({ type: null }); }}>{item.cityName} ({item.airportCode})</li>
                                                                                ))}
                                                                            </ul>
                                                                        )}
                                                                    </div>
                                                                    <span>→</span>
                                                                    <div className="dmc-autocomplete-wrapper">
                                                                        <input placeholder="To(DXB)" value={flight.to} onChange={(e) => { updateFlightOption(idx, 'to', e.target.value); setActiveDropdown({ type: 'to', fIdx: idx }); }} onFocus={() => setActiveDropdown({ type: 'to', fIdx: idx })} />
                                                                        {activeDropdown.type === 'to' && activeDropdown.fIdx === idx && flight.to.length > 0 && (
                                                                            <ul className="dmc-autocomplete-list">
                                                                                {AIRPORTLIST.filter(item => item.cityName?.toLowerCase().includes(flight.to.toLowerCase()) || item.airportCode?.toLowerCase().includes(flight.to.toLowerCase())).slice(0, 8).map(item => (
                                                                                    <li key={item.airportCode} onClick={() => { updateFlightOption(idx, 'to', item.airportCode); setActiveDropdown({ type: null }); }}>{item.cityName} ({item.airportCode})</li>
                                                                                ))}
                                                                            </ul>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <button className="dmc-pkg-itin-delete" onClick={() => removeFlightOption(idx)}><FontAwesomeIcon icon={faTrashAlt} /></button>
                                                            </div>

                                                            <div className="dmc-pkg-grid-row">
                                                                {/* --- MAIN AIRLINE SEARCH --- */}
                                                                <div className="dmc-pkg-field-group " style={{ position: 'relative' }}>
                                                                    <label className="dmc-pkg-label">Main Airline</label>
                                                                    <div className="dmc-autocomplete-wrapper">
                                                                        <input
                                                                            className="dmc-pkg-input"
                                                                            placeholder="Search Airline"
                                                                            value={flight.airline}
                                                                            onChange={(e) => {
                                                                                updateFlightOption(idx, 'airline', e.target.value);
                                                                                setActiveDropdown({ type: 'airline-main', fIdx: idx });
                                                                            }}
                                                                            onFocus={() => setActiveDropdown({ type: 'airline-main', fIdx: idx })}
                                                                        />
                                                                        {activeDropdown.type === 'airline-main' && activeDropdown.fIdx === idx && flight.airline.length > 0 && (
                                                                            <ul className="dmc-autocomplete-list">
                                                                                {AIRLINELIST.filter(a =>
                                                                                    a.name?.toLowerCase().includes(flight.airline.toLowerCase()) ||
                                                                                    a.code?.toString().toLowerCase().includes(flight.airline.toLowerCase())
                                                                                ).slice(0, 8).map(airline => (
                                                                                    <li key={airline.code} onClick={() => {
                                                                                        updateFlightOption(idx, 'airline', airline.name);
                                                                                        setActiveDropdown({ type: null });
                                                                                    }}>
                                                                                        <strong>{airline.code}</strong> - {airline.name}
                                                                                    </li>
                                                                                ))}
                                                                            </ul>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                <div className="dmc-pkg-field-group">
                                                                    <label className="dmc-pkg-label">Cabin Class</label>
                                                                    <select className="dmc-pkg-input" value={flight.cabin} onChange={(e) => updateFlightOption(idx, 'cabin', e.target.value)}>
                                                                        <option>Economy</option><option>Premium Economy</option><option>Business</option>
                                                                    </select>
                                                                </div>
                                                                <div className="dmc-pkg-field-group">
                                                                    <label className="dmc-pkg-label">Price PP</label>
                                                                    <input className="dmc-pkg-input" type="number" value={flight.pricePerPerson} onChange={(e) => updateFlightOption(idx, 'pricePerPerson', e.target.value)} />
                                                                </div>
                                                            </div>

                                                            {/* --- SEGMENTS WITH AIRLINE SEARCH --- */}
                                                            {/* --- UPDATED SEGMENTS SECTION --- */}
                                                            <div className="dmc-pkg-segments-section">
                                                                <p className="dmc-pkg-sub-label">Flight Segments / Connections</p>
                                                                {flight.segments.map((seg, sIdx) => (
                                                                    <div key={sIdx} className="dmc-segment-group-card">

                                                                        {/* DELETE BUTTON */}
                                                                        <button className="dmc-segment-delete-btn" title="Remove Segment" onClick={() => {
                                                                            const newOpts = [...packageData.flights.options];
                                                                            newOpts[idx].segments = newOpts[idx].segments.filter((_, i) => i !== sIdx);
                                                                            setPackageData(prev => ({ ...prev, flights: { ...prev.flights, options: newOpts } }));
                                                                        }}>
                                                                            <FontAwesomeIcon icon={faTimes} />
                                                                        </button>

                                                                        {/* THE GRID (3 columns x 2 rows automatically) */}
                                                                        <div className="dmc-segment-grid">

                                                                            {/* --- ROW 1 --- */}

                                                                            {/* 1. From */}
                                                                            <div className="dmc-autocomplete-wrapper">
                                                                                <span className="seg-grid-label">From</span>
                                                                                <input
                                                                                    className="dmc-pkg-input"
                                                                                    placeholder="From(HYD)"
                                                                                    value={seg.from}
                                                                                    onChange={(e) => {
                                                                                        updateFlightSegment(idx, sIdx, 'from', e.target.value.toUpperCase());
                                                                                        setActiveDropdown({ type: 'seg-from', fIdx: idx, sIdx });
                                                                                    }}
                                                                                    onFocus={() => setActiveDropdown({ type: 'seg-from', fIdx: idx, sIdx })}
                                                                                />
                                                                                {activeDropdown.type === 'seg-from' && activeDropdown.fIdx === idx && activeDropdown.sIdx === sIdx && seg.from?.length > 0 && (
                                                                                    <ul className="dmc-autocomplete-list">
                                                                                        {AIRPORTLIST.filter(item => item.cityName?.toLowerCase().includes(seg.from.toLowerCase()) || item.airportCode?.toLowerCase().includes(seg.from.toLowerCase())).slice(0, 5).map(item => (
                                                                                            <li key={item.airportCode} onClick={() => { updateFlightSegment(idx, sIdx, 'from', item.airportCode); setActiveDropdown({ type: null }); }}>
                                                                                                {item.airportCode} - {item.cityName}
                                                                                            </li>
                                                                                        ))}
                                                                                    </ul>
                                                                                )}
                                                                            </div>

                                                                            {/* 2. To */}
                                                                            <div className="dmc-autocomplete-wrapper">
                                                                                <span className="seg-grid-label">To</span>
                                                                                <input
                                                                                    className="dmc-pkg-input"
                                                                                    placeholder="To(DXB)"
                                                                                    value={seg.to}
                                                                                    onChange={(e) => {
                                                                                        updateFlightSegment(idx, sIdx, 'to', e.target.value.toUpperCase());
                                                                                        setActiveDropdown({ type: 'seg-to', fIdx: idx, sIdx });
                                                                                    }}
                                                                                    onFocus={() => setActiveDropdown({ type: 'seg-to', fIdx: idx, sIdx })}
                                                                                />
                                                                                {activeDropdown.type === 'seg-to' && activeDropdown.fIdx === idx && activeDropdown.sIdx === sIdx && seg.to?.length > 0 && (
                                                                                    <ul className="dmc-autocomplete-list">
                                                                                        {AIRPORTLIST.filter(item => item.cityName?.toLowerCase().includes(seg.to.toLowerCase()) || item.airportCode?.toLowerCase().includes(seg.to.toLowerCase())).slice(0, 5).map(item => (
                                                                                            <li key={item.airportCode} onClick={() => { updateFlightSegment(idx, sIdx, 'to', item.airportCode); setActiveDropdown({ type: null }); }}>
                                                                                                {item.airportCode} - {item.cityName}
                                                                                            </li>
                                                                                        ))}
                                                                                    </ul>
                                                                                )}
                                                                            </div>

                                                                            {/* 3. Airline */}
                                                                            <div className="dmc-autocomplete-wrapper">
                                                                                <span className="seg-grid-label">Airline</span>
                                                                                <input
                                                                                    className="dmc-pkg-input"
                                                                                    placeholder="Airline"
                                                                                    value={seg.airline}
                                                                                    onChange={(e) => {
                                                                                        updateFlightSegment(idx, sIdx, 'airline', e.target.value);
                                                                                        setActiveDropdown({ type: 'airline-seg', fIdx: idx, sIdx: sIdx });
                                                                                    }}
                                                                                    onFocus={() => setActiveDropdown({ type: 'airline-seg', fIdx: idx, sIdx: sIdx })}
                                                                                />
                                                                                {activeDropdown.type === 'airline-seg' && activeDropdown.fIdx === idx && activeDropdown.sIdx === sIdx && seg.airline?.length > 0 && (
                                                                                    <ul className="dmc-autocomplete-list">
                                                                                        {AIRLINELIST.filter(a => a.name?.toLowerCase().includes(seg.airline.toLowerCase()) || a.code?.toString().toLowerCase().includes(seg.airline.toLowerCase())).slice(0, 5).map(airline => (
                                                                                            <li key={airline.code} onClick={() => { updateFlightSegment(idx, sIdx, 'airline', airline.name); setActiveDropdown({ type: null }); }}>
                                                                                                {airline.code} - {airline.name}
                                                                                            </li>
                                                                                        ))}
                                                                                    </ul>
                                                                                )}
                                                                            </div>

                                                                            {/* --- ROW 2 --- */}

                                                                            {/* 4. Flight No */}
                                                                            <div>
                                                                                <span className="seg-grid-label">Flight No</span>
                                                                                <input className="dmc-pkg-input" placeholder="Flight No." value={seg.flightNo} onChange={(e) => updateFlightSegment(idx, sIdx, 'flightNo', e.target.value)} />
                                                                            </div>

                                                                            {/* 5. Departure */}
                                                                            <div>
                                                                                <span className="seg-grid-label">Departure Time</span>
                                                                                <input className="dmc-pkg-input" type="datetime-local" value={seg.departureTime} onChange={(e) => updateFlightSegment(idx, sIdx, 'departureTime', e.target.value)} />
                                                                            </div>

                                                                            {/* 6. Arrival */}
                                                                            <div>
                                                                                <span className="seg-grid-label">Arrival Time</span>
                                                                                <input className="dmc-pkg-input" type="datetime-local" value={seg.arrivalTime} onChange={(e) => updateFlightSegment(idx, sIdx, 'arrivalTime', e.target.value)} />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                                <button className="dmc-pkg-add-tier-link" onClick={() => addFlightSegment(idx)}>+ Add Connection Segment</button>
                                                            </div>
                                                        </div>
                                                    ))}

                                                    <button className="dmc-pkg-add-day-btn-dashed" onClick={addFlightOption}>
                                                        <FontAwesomeIcon icon={faPlus} /> Add Flight Option
                                                    </button>
                                                </div>
                                            )}

                                        </div>
                                    </div>
                                </div>
                                <div className="dmc-pkg-preview-container">
                                    <DMCPackagePreview data={packageData} onPublish={handleSavePackage} btnLoading={btnLoading} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DMCPackageCreator;